import { parse as parseDomain } from 'tldts'
import { z } from 'zod'

export const salesLeadBusinessTypes = [
	'Dealership Group',
	'Franchise Dealer',
	'Independent Dealer',
	'Manufacturer',
	'Agency or Service Provider',
	'Something else...',
] as const

const trimmedRequiredString = (label: string, maxLength: number) =>
	z
		.string({ required_error: `${label} is required` })
		.trim()
		.min(1, `${label} is required`)
		.max(maxLength, `${label} must be ${maxLength} characters or fewer`)

const PhoneSchema = z
	.string({ required_error: 'Phone number is required' })
	.trim()
	.min(1, 'Phone number is required')
	.max(32, 'Phone number must be 32 characters or fewer')
	.regex(
		/^\+?[\d().\-\s]+$/,
		'Phone number can only contain numbers and common phone punctuation',
	)
	.refine((value) => {
		const digitCount = value.replace(/\D/g, '').length
		return digitCount >= 7 && digitCount <= 15
	}, 'Please enter a phone number with 7 to 15 digits')

const WebsiteSchema = z
	.string({ required_error: 'Website is required' })
	.trim()
	.min(1, 'Website is required')
	.max(2048, 'Website must be 2,048 characters or fewer')
	.transform((value) =>
		/^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`,
	)
	.refine((value) => {
		try {
			const url = new URL(value)
			if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
			if (url.username || url.password) return false

			const hostname = url.hostname.toLowerCase()
			if (
				hostname === 'localhost' ||
				hostname.startsWith('[') ||
				/^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)
			) {
				return false
			}

			const labels = hostname.split('.')
			const parsedDomain = parseDomain(hostname, {
				allowPrivateDomains: false,
			})
			return (
				labels.length >= 2 &&
				(labels.every(
					(label) =>
						label.length > 0 &&
						label.length <= 63 &&
						/^[a-z\d](?:[a-z\d-]*[a-z\d])?$/i.test(label),
				) ||
					false) &&
				Boolean(parsedDomain.domain) &&
				parsedDomain.isIcann
			)
		} catch {
			return false
		}
	}, 'Please enter a valid public website')
	.transform((value) => new URL(value).toString())

export const SalesLeadSchema = z.object({
	firstName: trimmedRequiredString('First name', 80),
	lastName: trimmedRequiredString('Last name', 80),
	dealership: trimmedRequiredString('Company name', 120),
	email: z
		.string({ required_error: 'Email is required' })
		.trim()
		.min(1, 'Email is required')
		.max(254, 'Email must be 254 characters or fewer')
		.email('Please enter a valid email')
		.transform((value) => value.toLowerCase()),
	phone: PhoneSchema,
	dealerType: z.enum(salesLeadBusinessTypes, {
		required_error: 'Business type is required',
		invalid_type_error: 'Please select a valid business type',
	}),
	jobTitle: trimmedRequiredString('Job title', 120),
	website: WebsiteSchema,
	message: z
		.string({ required_error: 'Project details are required' })
		.trim()
		.min(10, 'Project details must be at least 10 characters')
		.max(4000, 'Project details must be 4,000 characters or fewer'),
})

export type SalesLead = z.infer<typeof SalesLeadSchema>
